<template>
  <div class="modal modal-active">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title">{{ _lang('Duplicate project') }}</div>
          <span class="close" @click="close">&times;</span>
        </div>

        <div class="modal-body">
          <div v-if="isLoading" class="text-center py-3">
            {{ _lang('Loading') }}...
          </div>

          <form v-else @submit.prevent="submit">
            <div class="form-group">
              <label>{{ _lang('Project name') }}</label>
              <input
                v-model.trim="form.name"
                type="text"
                class="form-control"
                required
                autofocus
              >
            </div>

            <template v-if="hasCollections">
              <div class="form-group mb-2">
                <label class="checkbox-row">
                  <input v-model="form.includeCollections" type="checkbox">
                  <span>{{ _lang('Include collections') }}</span>
                </label>
              </div>

              <div class="form-group mb-3">
                <label class="checkbox-row" :class="{ disabled: !form.includeCollections }">
                  <input
                    v-model="form.includePosts"
                    type="checkbox"
                    :disabled="!form.includeCollections"
                  >
                  <span>{{ _lang('Include posts') }}</span>
                </label>
              </div>
            </template>

            <div v-if="errorMessage" class="alert alert-danger">
              {{ errorMessage }}
            </div>

            <div class="actions">
              <button type="button" class="btn btn-light" @click="close" :disabled="isSubmitting">
                {{ _lang('Cancel') }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !form.name">
                {{ isSubmitting ? _lang('Duplicating') : _lang('Duplicate') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: ['project'],

  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      errorMessage: '',
      details: null,
      form: {
        name: this.defaultName(),
        includeCollections: true,
        includePosts: false,
      },
    };
  },

  computed: {
    hasCollections() {
      return Boolean(this.details && this.details.tables && this.details.tables.length);
    },
  },

  watch: {
    'form.includeCollections'(value) {
      if (!value) {
        this.form.includePosts = false;
      }
    },
  },

  methods: {
    defaultName() {
      const copyLabel = this._lang('(copy)');
      const escapedCopyLabel = copyLabel.replace(/[|\\{}()[\]^$+*?.-]/g, '\\$&');
      const match = this.project.name.match(new RegExp(`^(.*) ${escapedCopyLabel}(?: ([0-9]+))?$`));

      if (!match) {
        return `${this.project.name} ${copyLabel}`;
      }

      const baseName = match[1];
      const nextCopyNumber = match[2] ? Number(match[2]) + 1 : 2;
      return `${baseName} ${copyLabel} ${nextCopyNumber}`;
    },
    close() {
      if (this.isSubmitting) return;
      this.$emit('close');
    },
    loadProject() {
      this.isLoading = true;
      this.errorMessage = '';

      axios({
        url: `/api/projects/${this.project.id}`,
      }).then((res) => {
        this.details = res.data;
      }).catch((err) => {
        this.errorMessage = err.response && err.response.data ? err.response.data : err.message;
      }).finally(() => {
        this.isLoading = false;
      });
    },
    submit() {
      this.errorMessage = '';
      this.isSubmitting = true;

      axios({
        method: 'post',
        url: `/api/projects/${this.project.id}/duplicate`,
        data: {
          name: this.form.name,
          includeCollections: this.hasCollections ? this.form.includeCollections : false,
          includePosts: this.hasCollections ? this.form.includePosts : false,
        },
      }).then((res) => {
        this.$emit('duplicated', res.data);
        this.$emit('close');
      }).catch((err) => {
        this.errorMessage = err.response && err.response.data ? err.response.data : err.message;
      }).finally(() => {
        this.isSubmitting = false;
      });
    },
  },

  mounted() {
    this.loadProject();
  },
};
</script>

<style scoped>
.checkbox-row {
  align-items: center;
  display: flex;
  gap: 8px;
  margin: 0;
}

.checkbox-row.disabled {
  opacity: 0.6;
}

.actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
</style>
